import { Flex } from "antd";
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    getCompanyDebtListByOwnerMarketIdThunk,
    getInvoiceDebtListThunk,
} from "tm-shared/api";
import { formatUAHStandard } from "tm-shared/utils";

import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { BlockLoader } from "@/components/UI/BlockLoader";
import { UniversalTabs } from "@/components/UniversalTabs";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useAsyncTaskQueue } from "@/hooks/useAsyncTaskQueue";

import { StyledDebtContainer } from "../Styled";

interface IDebtTab {
    hasDebt: boolean;
}

interface DebtItem {
    label: string;
    amount: number;
    date: string;
}

const TABS = [
    { label: "По магазину", value: "tt" },
    // { label: "По контрагенту", value: "fop" },
];

const NoDebtInfo: FC = () => (
    <StyledDebtContainer>
        <SecondaryText>Борг відсутній</SecondaryText>
    </StyledDebtContainer>
);

const DebtList: FC<{ debts: DebtItem[] }> = ({ debts }) => (
    <StyledDebtContainer>
        {debts.map(({ amount, label, date }) => (
            <Flex key={label} gap={8} align="center" justify="space-between">
                <SecondaryText>
                    {label} ({date})
                </SecondaryText>
                <PrimaryText>{formatUAHStandard(amount)} </PrimaryText>
            </Flex>
        ))}
    </StyledDebtContainer>
);

export const DebtTab: FC<IDebtTab> = ({ hasDebt }) => {
    const dispatch = useAppDispatch();
    const { marketId } = useParams<{ marketId: string }>();
    const safeMarketId = marketId ?? "";

    const { debts } = useAppSelector((state) => state.work);
    const { debtsByFOP } = useAppSelector((state) => state.offline);

    const [activeTab, setActiveTab] = useState(TABS[0].value);

    const totalDebt = useMemo(() => {
        if (!hasDebt) return 0;
        return Object.values(debts)
            .filter((item) => item.marketId === safeMarketId)
            .reduce((acc, item) => acc + item.debt, 0);
    }, [debts, safeMarketId, hasDebt]);

    const { ttDebts, fopDebts } = useMemo(
        () => ({
            ttDebts: Object.values(debts),
            fopDebts: Object.values(debtsByFOP[safeMarketId] || {}),
        }),
        [debts, debtsByFOP, safeMarketId],
    );

    const { queue, progress } = useAsyncTaskQueue();

    useEffect(() => {
        if (hasDebt) {
            queue.add("", () =>
                dispatch(
                    getInvoiceDebtListThunk({ marketId: safeMarketId }),
                ).unwrap(),
            );
        }

        queue.add("", () =>
            dispatch(
                getCompanyDebtListByOwnerMarketIdThunk({
                    marketId: safeMarketId,
                }),
            ).unwrap(),
        );

        queue.runParallel();
    }, [dispatch, hasDebt, safeMarketId]);

    const renderDebtSummary = () => {
        if (!hasDebt) {
            return <NoDebtInfo />;
        }

        return (
            <StyledDebtContainer>
                <Flex gap={8} align="center" justify="space-between">
                    <SecondaryText>Залишок боргу</SecondaryText>
                    {totalDebt > 0 && (
                        <PrimaryText>
                            {formatUAHStandard(totalDebt)}
                        </PrimaryText>
                    )}
                </Flex>
            </StyledDebtContainer>
        );
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case "tt": {
                const ttData = ttDebts.map((item) => ({
                    label: item.consignmentId,
                    amount: item.debt ?? 0,
                    date: dayjs(item?.creationDate).format("DD.MM.YYYY"),
                }));

                return ttData.length > 0 ? (
                    <DebtList debts={ttData} />
                ) : (
                    <NoDebtInfo />
                );
            }

            case "fop": {
                const fopData: DebtItem[] = fopDebts.map((item) => ({
                    label: item.consignmentId,
                    amount: item.debt ?? 0,
                    date: dayjs(item?.creationDate).format("DD.MM.YYYY"),
                }));

                return fopData.length > 0 ? (
                    <DebtList debts={fopData} />
                ) : (
                    <NoDebtInfo />
                );
            }

            default:
                return null;
        }
    };

    if (progress.isRunning) {
        return <BlockLoader />;
    }

    return (
        <Flex vertical gap={16}>
            {renderDebtSummary()}

            <UniversalTabs
                tabs={TABS}
                value={activeTab}
                maxWidth="100%"
                onChange={setActiveTab}
            />

            {renderTabContent()}
        </Flex>
    );
};
